import { useCallback, useEffect, useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";
import css from "./MoveButton.module.css";

/**
 * Dual-mode footer button for edit-state:
 *  - Tap → действие текущего режима: onMove() (move-режим основной сетки,
 *    A48-коррекция) или onCustomTime() (CustomTimeModal у морфнутой).
 *  - Long-press ≥ 480ms (стандарт жестов A43) — ПЕРЕКЛЮЧАТЕЛЬ и ТОЛЬКО он:
 *    «ПЕРЕНЕСТИ» ⇄ «НЕ СТАНДАРТНЫЙ СЛОТ» (13.08-2: авто-реверт убран).
 *
 * 13.08-3: действие переведено с pointerup на СИНТЕТИЧЕСКИЙ click — на части
 * Android-прошивок pointerup после длинного/смазанного жеста не доезжает до
 * кнопки, и тап «умирал» (жалоба: тап по морфнутой не открывал барабаны).
 * click браузер шлёт всегда; long-press ловится таймером от pointerdown, его
 * хвостовой click глотается флагом longFired (сбрасывается на новом жесте —
 * прошивки, где click после long-press не приходит, не съедают следующий тап).
 */
export interface MoveButtonProps {
  onMove: () => void;
  onCustomTime: () => void;
  disabled?: boolean;
}

const LONG_PRESS_MS = 480;

export function MoveButton({ onMove, onCustomTime, disabled }: MoveButtonProps) {
  const [morphed, setMorphed] = useState(false);
  const [holding, setHolding] = useState(false);
  const pressTimer = useRef<number | null>(null);
  const longFired = useRef(false);

  const clearTimers = useCallback(() => {
    if (pressTimer.current) {
      window.clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
  }, []);

  /* Unmount (Esc/закрытие модалки во время удержания) — таймер не должен пережить кнопку. */
  useEffect(() => () => clearTimers(), [clearTimers]);

  /* disabled во время удержания: pointerup по disabled-кнопке не диспатчится —
     гасим holding/таймер руками. */
  useEffect(() => {
    if (disabled) { setHolding(false); clearTimers(); }
  }, [disabled, clearTimers]);

  const handlePointerDown = useCallback((e: ReactPointerEvent<HTMLButtonElement>) => {
    if (disabled) return;
    /* БМ4-фикс: capture — up/click доезжают при микро-сдвиге пальца. */
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* указатель уже неактивен */ }
    clearTimers(); // мультитач: второй палец не роняет таймер первого
    longFired.current = false; // новый жест: хвост прошлого long-press не глотает этот тап
    setHolding(true);
    pressTimer.current = window.setTimeout(() => {
      /* Long-press — переключатель в обе стороны; его хвостовой click глотаем. */
      longFired.current = true;
      setMorphed((m) => !m);
      setHolding(false);
      pressTimer.current = null;
    }, LONG_PRESS_MS);
  }, [disabled, clearTimers]);

  const handlePointerEnd = useCallback(() => {
    setHolding(false);
    clearTimers();
  }, [clearTimers]);

  const handleClick = useCallback(() => {
    if (disabled) return;
    if (longFired.current) { longFired.current = false; return; } // хвост long-press
    /* Short tap: действие текущего режима; режим меняет ТОЛЬКО long-press. */
    if (morphed) onCustomTime();
    else onMove();
  }, [disabled, morphed, onMove, onCustomTime]);

  return (
    <button
      type="button"
      className={`${css.btn} ${morphed ? css.btnCustom : css.btnMove} ${holding ? css.holding : ""}`}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerEnd}
      onPointerCancel={handlePointerEnd}
      onPointerLeave={handlePointerEnd}
      onClick={handleClick}
      /* Android long-press зовёт контекстное меню — оно рвёт жест переключателя. */
      onContextMenu={(e) => e.preventDefault()}
      disabled={disabled}
      aria-label={morphed ? "Нестандартный слот" : "Перенести"}
    >
      <span className={css.label}>
        {morphed ? "НЕ СТАНДАРТНЫЙ СЛОТ" : "ПЕРЕНЕСТИ"}
      </span>
      {holding ? (
        <span className={css.holdHint} aria-hidden="true" />
      ) : null}
    </button>
  );
}
